// Copyright (c) 2020 WiseTime. All rights reserved.

import { DateTime } from "luxon"
import { TimeBlocksSortOrder, TimelineRowSortType } from "generated/timeline/toolbar/toolbar_pb"
import { TimeGroup, TimeRow } from "generated/wisetime/user/timeline/v4/timeline_pb"
import { TagRef } from "generated/wisetime/user/type/tag_ref_pb"
import { isTimeRow, TimelineRow } from "./timeBlocks"

export type SortTypeStrategy = (timelineRow1: TimelineRow, timelineRow2: TimelineRow) => number

// The order of time rows within a time block should be predictable, and the default chronological sorting relies on
// this sorting strategy.
const chronologicalSort = (timelineRow1: TimelineRow, timelineRow2: TimelineRow) => {
  if (timelineRow1.firstObservedInHour === timelineRow2.firstObservedInHour
    && !isTimeRow(timelineRow1)
    && isTimeRow(timelineRow2)) {
    return -1
  }
  return timelineRow1.firstObservedInHour - timelineRow2.firstObservedInHour
}

// Rows without tags are at the end
const withoutTagsToEndOfList = (timeRow1: TimeRow.AsObject, timeRow2: TimeRow.AsObject) => {
  if (timeRow1.tagsList.length === 0 || timeRow2.tagsList.length === 0) {
    return timeRow2.tagsList.length - timeRow1.tagsList.length
  }
  return 0
}

export const sortTagsListAlphabetically = (tag1: TagRef.AsObject, tag2: TagRef.AsObject): number =>
  tag1.name.localeCompare(tag2.name)

// Rows with tags are sorted by first tag alphabetically
const compareFirstTagAlphabetically = (timeRow1: TimeRow.AsObject, timeRow2: TimeRow.AsObject) => {
  if (timeRow1.tagsList.length === 0 || timeRow2.tagsList.length === 0) {
    return 0
  }
  const [firstSortedTagRow1] = [...timeRow1.tagsList].sort(sortTagsListAlphabetically)
  const [firstSortedTagRow2] = [...timeRow2.tagsList].sort(sortTagsListAlphabetically)
  if (!firstSortedTagRow1 || !firstSortedTagRow2) {
    return 0
  }
  return firstSortedTagRow1.name.localeCompare(firstSortedTagRow2.name)
}

// If first tag is same - rows sorted by number of tags ASC
const withAdditionalTagsToEnd = (timeRow1: TimeRow.AsObject, timeRow2: TimeRow.AsObject) => {
  return timeRow1.tagsList.length - timeRow2.tagsList.length
}

// If number of tags is same - all tags compared alphabetically one by one
const compareAdditionalTagsAlphabetically = (timeRow1: TimeRow.AsObject, timeRow2: TimeRow.AsObject) => {
  const sortedTagsRow1 = [...timeRow1.tagsList].sort(sortTagsListAlphabetically)
  const sortedTagsRow2 = [...timeRow2.tagsList].sort(sortTagsListAlphabetically)
  for (let i = 0; i < sortedTagsRow1.length && i < sortedTagsRow2.length; i++) {
    const sortedTagRow1 = sortedTagsRow1[i]
    const sortedTagRow2 = sortedTagsRow2[i]
    if (!sortedTagRow1 || !sortedTagRow2) {
      return 0
    }
    const result = sortedTagRow1.name.localeCompare(sortedTagRow2.name)
    if (result != 0) {
      return result
    }
  }
  return 0
}

// Rows that belong to one time group are always together
const sortByTimeGroupNameEmptyToEnd = (timeRow1: TimeRow.AsObject, timeRow2: TimeRow.AsObject) => {
  if (timeRow1.timeGroupId.length === 0 || timeRow2.timeGroupId.length === 0) {
    return timeRow2.timeGroupId.length - timeRow1.timeGroupId.length
  }
  return timeRow1.timeGroupId.localeCompare(timeRow2.timeGroupId)
}

/*
TAG Sorting Logic ported from the original console project: https://stash.practiceinsight.io/stash/projects/WT/repos/wise-time/browse/src/main/java/io/wisetime/console/client/application/widgets/TimeBlockContentPresenterHelper.java
Time rows sorted by following rules:
1. Rows that belong to one time group are always together
2. Rows without tags are at the end
3. Rows with tags are sorted by first tag alphabetically
4. If first tag is same - rows sorted by number of tags ASC
5. If number of tags is same - all tags compared alphabetically one by one
*/
export const tagStrategy = (timeRow1: TimeRow.AsObject, timeRow2: TimeRow.AsObject) => {
  const comparators = [
    withoutTagsToEndOfList,
    compareFirstTagAlphabetically,
    withAdditionalTagsToEnd,
    compareAdditionalTagsAlphabetically,
    sortByTimeGroupNameEmptyToEnd
  ]
  for (const comparator of comparators) {
    const compare = comparator(timeRow1, timeRow2)
    if (compare !== 0) {
      return compare
    }
  }
  return 0
}

export const getSortTypeStrategy = (timeBlocksSortOrder: TimeBlocksSortOrder, timeRowSortType: TimelineRowSortType): SortTypeStrategy =>
  (timelineRow1: TimelineRow, timelineRow2: TimelineRow) => {
    if (timeRowSortType === TimelineRowSortType.CHRONOLOGY || timeRowSortType === TimelineRowSortType.REVERSE_CHRONOLOGY) {
      // As part of the initial grouping process, time rows are sorted by the activity hour, which is in UTC.
      if (timelineRow1.activityHour > timelineRow2.activityHour) {
        return timeBlocksSortOrder === TimeBlocksSortOrder.ASCENDING ? 1 : -1
      } else if (timelineRow1.activityHour < timelineRow2.activityHour) {
        return timeBlocksSortOrder === TimeBlocksSortOrder.ASCENDING ? -1 : 1
      }
      // The TimeZoneOffset is then used to separate the activities performed during the same activity hour, but in a
      // different timezone.
      if (timelineRow1.timeZoneOffset > timelineRow2.timeZoneOffset) {
        return timeBlocksSortOrder === TimeBlocksSortOrder.ASCENDING ? 1 : -1
      } else if (timelineRow1.timeZoneOffset < timelineRow2.timeZoneOffset) {
        return timeBlocksSortOrder === TimeBlocksSortOrder.ASCENDING ? -1 : 1
      }
    }
    return sortTypeStrategies[timeRowSortType](timelineRow1, timelineRow2)
  }
  
const sortTimeMarkersByTimeAndPushToEnd = (timelineRow1: TimelineRow, timelineRow2: TimelineRow): number => {
  if (!isTimeRow(timelineRow1) && isTimeRow(timelineRow2)) {
    return 1
  }
  if (isTimeRow(timelineRow1) && !isTimeRow(timelineRow2)) {
    return -1
  }
  if (!isTimeRow(timelineRow1) && !isTimeRow(timelineRow2)) {
    return timelineRow1.activityHour === timelineRow2.activityHour ?
      (timelineRow1.startTime?.seconds ?? 0) - (timelineRow2.startTime?.seconds ?? 0) :
      timelineRow1.activityHour - timelineRow2.activityHour
  }
  return 0
}
export const sortTypeStrategies: Record<TimelineRowSortType, SortTypeStrategy> = {
  [TimelineRowSortType.CHRONOLOGY]: (timelineRow1, timelineRow2) =>
    chronologicalSort(timelineRow1, timelineRow2),
  [TimelineRowSortType.REVERSE_CHRONOLOGY]: (timelineRow1, timelineRow2) =>
    chronologicalSort(timelineRow2, timelineRow1),
  [TimelineRowSortType.DURATION]: (timelineRow1, timelineRow2) =>
    isTimeRow(timelineRow1) && isTimeRow(timelineRow2) ?
      (timelineRow1.duration?.seconds ?? 0) - (timelineRow2.duration?.seconds ?? 0) :
      sortTimeMarkersByTimeAndPushToEnd(timelineRow1, timelineRow2),
  [TimelineRowSortType.ACTIVITY]: (timelineRow1, timelineRow2) =>
    isTimeRow(timelineRow1) && isTimeRow(timelineRow2) ?
      timelineRow1.activity.localeCompare(timelineRow2.activity) :
      sortTimeMarkersByTimeAndPushToEnd(timelineRow1, timelineRow2),
  [TimelineRowSortType.DESCRIPTION]: (timelineRow1, timelineRow2) =>
    isTimeRow(timelineRow1) && isTimeRow(timelineRow2) ?
      timelineRow1.description.localeCompare(timelineRow2.description) :
      sortTimeMarkersByTimeAndPushToEnd(timelineRow1, timelineRow2),
  [TimelineRowSortType.TAG]: (timelineRow1, timelineRow2) =>
    isTimeRow(timelineRow1) && isTimeRow(timelineRow2) ?
      tagStrategy(timelineRow1, timelineRow2) :
      sortTimeMarkersByTimeAndPushToEnd(timelineRow1, timelineRow2),
}

export const sortTimeGroupsByCreateTime = (group1: TimeGroup.AsObject, group2: TimeGroup.AsObject): number => {
  const timeDifference = (group1.createTime?.seconds ?? 0) - (group2.createTime?.seconds ?? 0)
  return timeDifference === 0 ? group1.id.localeCompare(group2.id)
    : timeDifference
}

export const sortGroupedTimeRows = (timeRows: TimeRow.AsObject[], sortType: TimelineRowSortType) => {
  const sortStrategy = (timeRow1: TimeRow.AsObject, timeRow2: TimeRow.AsObject) => {
    if (sortType === TimelineRowSortType.DURATION
      || sortType === TimelineRowSortType.ACTIVITY
      || sortType === TimelineRowSortType.DESCRIPTION) {
      return sortTypeStrategies[sortType](timeRow1, timeRow2)
    }
    const seconds = (timeRow: TimeRow.AsObject) =>
      DateTime.fromFormat(timeRow.activityHour.toString(), "yyyyMMddHH")
        .plus({ minutes: timeRow.firstObservedInHour, hours: timeRow.timeZoneOffset })
        .toSeconds()
    const seconds1 = seconds(timeRow1)
    const seconds2 = seconds(timeRow2)
    // Fallback to TimelineRowSortType.CHRONOLOGY
    return sortType === TimelineRowSortType.REVERSE_CHRONOLOGY
      ? seconds2 - seconds1 : seconds1 - seconds2
  }
  return [...timeRows].sort(sortStrategy)
}
